import { BufferstockModel, StockPositionModel } from 'src/packages/warehouse';
import { firstValueFrom } from 'rxjs';
import { Adapter } from './adapter';
import { BookingPosition } from '../../types';
import { Log } from '../../util/log';

/**
 * Liest/schreibt Bufferstock-Bestände und -Disposals im lokalen Bestand.
 */
export class BufferstockAdapter extends Adapter {
  /**
   * Lädt einen Bufferstock aus dem lokalen Bestand.
   *
   * @param id Bufferstock-ID
   * @returns Der Bufferstock oder `null`, wenn er fehlt oder nicht lesbar ist.
   */
  public async findLocal(id: number): Promise<BufferstockModel | null> {
    try {
      const result = await firstValueFrom(
        this.services.bufferstockService.find(id),
      );
      return result ?? null;
    } catch (error) {
      Log.error('BufferstockAdapter.findLocal', error, { id });
      return null;
    }
  }

  /**
   * Schreibt den Bufferstock mit frischem Zeitstempel in den lokalen Bestand.
   *
   * @param bufferstock Zu speichernder Bufferstock
   * @returns `false`, wenn das Speichern fehlschlägt.
   */
  public async saveLocalForce(bufferstock: BufferstockModel): Promise<boolean> {
    try {
      await this.services.bufferstockService.saveLocalForce(bufferstock);
      return true;
    } catch (error) {
      Log.error('BufferstockAdapter.saveLocalForce', error, {
        id: bufferstock.id,
      });
      return false;
    }
  }

  /**
   * Lokal gebuchte Menge eines Artikels in einem Bufferstock.
   *
   * @param bufferstockId Bufferstock-ID
   * @param articleId Artikel-ID
   * @returns Die Menge, oder 0, wenn Position oder Bufferstock fehlen.
   */
  public async getArticleAmountLocal(
    bufferstockId: number,
    articleId: number,
  ): Promise<number> {
    const bufferstock = await this.findLocal(bufferstockId);
    return (
      bufferstock?.positions?.find(p => p.article_id === articleId)?.amount ?? 0
    );
  }

  /**
   * Verändert die lokale Position eines Artikels um `delta` (legt sie bei Bedarf
   * an) und speichert den Bufferstock.
   *
   * @param position Buchungsposition (liefert die Artikel-ID)
   * @param bufferstockId Ziel-Bufferstock
   * @param delta Bestandsänderung (negativ = Abgang)
   * @returns `false`, wenn der Bufferstock fehlt oder das Speichern fehlschlägt.
   */
  public async updatePositionAmountLocal(
    position: BookingPosition,
    bufferstockId: number,
    delta: number,
  ): Promise<boolean> {
    const bufferstock = await this.findLocal(bufferstockId);
    if (!bufferstock) {
      return false;
    }
    if (!bufferstock.positions) {
      bufferstock.positions = [];
    }
    const existing = bufferstock.positions?.find(
      pos => pos.article_id === position.article_id,
    );
    if (existing) {
      existing.amount += delta;
    } else {
      const article = await this.adapters.article.findLocal(
        position.article_id,
      );
      bufferstock.positions.push({
        article_id: position.article_id,
        // Keine Pflichtfelder, werden ggf. beim nächsten Sync nachgezogen
        article_name: article?.name,
        article_number: article?.article_number,
        amount: delta,
      } as StockPositionModel);
    }
    return this.saveLocalForce(bufferstock);
  }

  /**
   * Erhöht den Disposal-Eintrag (`position_disposals`) eines Artikels am
   * Bufferstock um `amount` (legt ihn bei Bedarf an).
   *
   * @param bufferstockId Bufferstock mit dem Disposal-Anker
   * @param articleId Artikel-ID
   * @param amount Zu addierende Disposal-Menge (0 = No-op)
   * @returns `false`, wenn Bufferstock/Artikel fehlen oder das Speichern fehlschlägt.
   */
  public async addDisposalLocal(
    bufferstockId: number,
    articleId: number,
    amount: number,
  ): Promise<boolean> {
    if (amount === 0) {
      return true;
    }
    const bufferstock = await this.findLocal(bufferstockId);
    if (!bufferstock) {
      return false;
    }
    const article = await this.adapters.article.findLocal(articleId);
    if (!article) {
      return false;
    }
    const existing = bufferstock.position_disposals?.find(
      disposal => disposal.article_id === articleId,
    );
    if (existing) {
      existing.amount = (existing.amount ?? 0) + amount;
    } else {
      bufferstock.position_disposals = bufferstock.position_disposals ?? [];
      bufferstock.position_disposals.push({
        amount,
        article,
        bufferstock,
        article_id: articleId,
        bufferstock_id: bufferstockId,
      });
    }
    return this.saveLocalForce(bufferstock);
  }
}
